import { OnboardingDocumentType } from '@dtos/document-type';
import { ISection1CandidateDetail } from '@models/onboarding-review/section1-candidate-detail';
import {
    I9DocumentType,
    ListADocuments
} from '@models/onboarding-review/section2-document-upload';
import { IConfigurationValuesService } from '@services/ConfigurationValuesService';
import { createConfigurationValuesService } from '@services/factories';
import { getTodayISOTimestamp } from '@utils/date-query';
import {
    isSocialSecurityNumberValid,
    stripSocialSecurityNumberFormatting
} from '@utils/pid-validation';
import { DocumentNumberLookup } from '../constants/document-number-lookup';
import { ISection2DocumentTypesValidation } from '@stores/onboarding-review/section2-document-types-validation-store';

type DocumentNumberValidationResult =
    | { valid: false; messageTemplate: string; templateArguments: string[]; }
    | { valid: true };

export interface IValidateDocumentDetailService {
    validateDocumentNumber(
        documentType: OnboardingDocumentType,
        value: string,
        documentDetails: ISection2DocumentTypesValidation | undefined
    ): DocumentNumberValidationResult;
    checkDocumentExpiryRequired(documentType: OnboardingDocumentType, documents: OnboardingDocumentType[]): boolean;
    /**
     * Sanitise a document number for submission according to the current rules
     * @param documentType
     * @param value
     */
    sanitiseDocumentNumberForSubmission(documentType: string, value: string): string;
    /**
     * Decide the minimum document expiry date for a given document type and list of document types
     * @param documentDetails
     */
    decideMinimumDocumentExpiry(documentDetails: ISection2DocumentTypesValidation | undefined): string | null;
}

export enum DocumentDetailValidationErrorTemplate {
    InvalidWithExample = 'invalidWithExample',
    SSNMismatch = 'ssnMismatch',
    SSNInvalidPattern = 'ssnInvalidPattern'
}

const SSN_DOCUMENT_TYPES: OnboardingDocumentType[] = [
    OnboardingDocumentType.SOCIAL_SECURITY_CARD_UNRESTRICTED,
    OnboardingDocumentType.RECEIPT_REPLACEMENT_SS_CARD_UNRESTRICTED
];

export default class ValidateDocumentDetailService implements IValidateDocumentDetailService {
    constructor(
        private lookupTable: Partial<Record<OnboardingDocumentType, DocumentNumberLookup>>,
        private candidateDetails: ISection1CandidateDetail,
        private configurationValuesService: IConfigurationValuesService
    ) {
    }

    private lookupRegexWithExampleAndRequirement(documentType: OnboardingDocumentType, value: string, documentDetails: ISection2DocumentTypesValidation | undefined):
     DocumentNumberValidationResult | null {

        const lookup = documentDetails?.documentRegex;
        if (!lookup) {
            return null;
        }

        if (RegExp(lookup).test(value)) {
            return {
                valid: true,
            };
        }

        return {
            valid: false,
            messageTemplate: DocumentDetailValidationErrorTemplate.InvalidWithExample,
            templateArguments: [
                 documentDetails?.documentNumberRequirements ?? '',
                documentDetails?.example ?? ''
            ]
        };
    }

    private lookupSocialSecurityNumberPattern(documentType: OnboardingDocumentType, value: string): DocumentNumberValidationResult | null {
        if (!SSN_DOCUMENT_TYPES.includes(documentType)) return null;
        const strippedValue = stripSocialSecurityNumberFormatting(value);
        if (isSocialSecurityNumberValid(strippedValue)) {
            return {
                valid: true
            }
        }

        return {
            valid: false,
            messageTemplate: DocumentDetailValidationErrorTemplate.SSNInvalidPattern,
            templateArguments: []
        };
    }

    private lookupSocialSecurityNumberMismatch(documentType: OnboardingDocumentType, value: string): DocumentNumberValidationResult | null {
        // SSN cards must match the previously entered SSN from Section 1
        if (!SSN_DOCUMENT_TYPES.includes(documentType)) return null;
        const strippedValue = stripSocialSecurityNumberFormatting(value);
        if (strippedValue === this.candidateDetails.socialSecurityNumber) {
            return {
                valid: true
            };
        }

        return {
            valid: false,
            messageTemplate: DocumentDetailValidationErrorTemplate.SSNMismatch,
            templateArguments: []
        };
    }

    validateDocumentNumber(documentType: OnboardingDocumentType, value: string, documentDetails: ISection2DocumentTypesValidation | undefined): DocumentNumberValidationResult {
        const withExampleAndRequirement = this.lookupRegexWithExampleAndRequirement(documentType, value, documentDetails);
        if (withExampleAndRequirement !== null) {
            return withExampleAndRequirement;
        }

        const ssnPattern = this.lookupSocialSecurityNumberPattern(documentType, value);
        if (ssnPattern !== null && !ssnPattern.valid) {
            return ssnPattern;
        }

        const configurationValuesService: IConfigurationValuesService = createConfigurationValuesService();
        if (configurationValuesService.showSection1) {
          const ssnMismatch = this.lookupSocialSecurityNumberMismatch(
            documentType,
            value
          );
          if (ssnMismatch !== null) {
            return ssnMismatch;
          }
        }

        // assume documents that don't have a rule are valid
        return {
            valid: true
        };
    }

    checkDocumentExpiryRequired(
        documentType: OnboardingDocumentType,
        documents: OnboardingDocumentType[]
    ): boolean {
        const isListADocument = this.configurationValuesService.expirationDateForAllListA  && ListADocuments.includes(documentType as string as I9DocumentType) 
        return this.lookupTable[documentType]?.requireExpirationDate(documents) ?? isListADocument;
    }

    sanitiseDocumentNumberForSubmission(documentType: string, value: string): string {
        // Only need sanitise for SSNs so far
        if (!SSN_DOCUMENT_TYPES.includes(documentType as OnboardingDocumentType)) return value;

        return stripSocialSecurityNumberFormatting(value);
    }

    decideMinimumDocumentExpiry(documentDetail: ISection2DocumentTypesValidation | undefined): string | null {

        if (!documentDetail?.isExpiredDocumentAllowed) {
            return getTodayISOTimestamp();
        }

        return null;
    }
}



// WEBPACK FOOTER //
// ./src/services/validate-document-detail-service.ts